import React, { useState, useEffect, useMemo } from 'react';
import { X, Percent, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TakeProfitStopLossModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'takeProfit' | 'stopLoss';
  side: 'long' | 'short';
  entryPrice: number;
  amount: number;
  leverage: number;
  lotSize?: number;
  variant?: 'vertical' | 'horizontal';
  onConfirm: (triggerPrice: number, executionType: 'market' | 'limit', executionPrice?: number) => void;
}

type InputMode = 'price' | 'pnl' | 'percentage';

const TakeProfitStopLossModal: React.FC<TakeProfitStopLossModalProps> = ({
  isOpen,
  onClose,
  type,
  side,
  entryPrice,
  amount,
  leverage,
  lotSize = 1,
  variant = 'vertical',
  onConfirm
}) => {
  const { t } = useTranslation();
  const [inputMode, setInputMode] = useState<InputMode>('price');
  const [priceValue, setPriceValue] = useState('');
  const [pnlValue, setPnlValue] = useState('');
  const [percentageValue, setPercentageValue] = useState('');
  const [executionType, setExecutionType] = useState<'market' | 'limit'>('market');
  const [executionPrice, setExecutionPrice] = useState('');

  const isTP = type === 'takeProfit';

  useEffect(() => {
    if (!isOpen) {
      setPriceValue('');
      setPnlValue('');
      setPercentageValue('');
      setExecutionPrice('');
      setInputMode('price');
      setExecutionType('market');
    }
  }, [isOpen]);

  const actualPositionSize = amount * lotSize;

  const calculatePriceFromPnL = (targetPnL: number): number => {
    if (side === 'long') {
      return entryPrice + (targetPnL / actualPositionSize);
    } else {
      return entryPrice - (targetPnL / actualPositionSize);
    }
  };

  const calculatePriceFromROI = (targetROI: number): number => {
    const margin = (actualPositionSize * entryPrice) / leverage;
    const targetPnL = (targetROI / 100) * margin;

    if (side === 'long') {
      return entryPrice + (targetPnL / actualPositionSize);
    } else {
      return entryPrice - (targetPnL / actualPositionSize);
    }
  };

  const calculatePnLFromPrice = (price: number): number => {
    if (side === 'long') {
      return (price - entryPrice) * actualPositionSize;
    } else {
      return (entryPrice - price) * actualPositionSize;
    }
  };

  const calculatePercentageFromPrice = (price: number): number => {
    return Math.abs(((price - entryPrice) / entryPrice) * 100);
  };

  const calculatedValues = useMemo(() => {
    let triggerPrice = 0;
    let pnl = 0;
    let percentage = 0;
    let roi = 0;

    if (inputMode === 'price' && priceValue) {
      triggerPrice = parseFloat(priceValue);
      pnl = calculatePnLFromPrice(triggerPrice);
      percentage = calculatePercentageFromPrice(triggerPrice);
    } else if (inputMode === 'pnl' && pnlValue) {
      pnl = parseFloat(pnlValue);
      if (!isTP) {
        pnl = -Math.abs(pnl);
      }
      triggerPrice = calculatePriceFromPnL(pnl);
      percentage = calculatePercentageFromPrice(triggerPrice);
    } else if (inputMode === 'percentage' && percentageValue) {
      roi = parseFloat(percentageValue);
      if (!isTP) {
        roi = -Math.abs(roi);
      }
      triggerPrice = calculatePriceFromROI(roi);
      pnl = calculatePnLFromPrice(triggerPrice);
      percentage = calculatePercentageFromPrice(triggerPrice);
    }

    if (triggerPrice > 0 && inputMode !== 'percentage') {
      const margin = (actualPositionSize * entryPrice) / leverage;
      roi = (pnl / margin) * 100;
    }

    return { triggerPrice, pnl, percentage, roi };
  }, [inputMode, priceValue, pnlValue, percentageValue, entryPrice, amount, leverage, side, actualPositionSize]);

  const handleInputChange = (mode: InputMode, value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, '');
    if ((sanitized.match(/\./g) || []).length > 1) return;

    if (mode === 'price') {
      setPriceValue(sanitized);
    } else if (mode === 'pnl') {
      setPnlValue(sanitized);
    } else if (mode === 'percentage') {
      setPercentageValue(sanitized);
    }
  };

  const isValid = calculatedValues.triggerPrice > 0 && !isNaN(calculatedValues.triggerPrice);

  const validateTPSL = (): string | null => {
    if (!isValid) return 'Please enter a valid value';

    const { triggerPrice, pnl } = calculatedValues;

    if (isTP) {
      if (pnl <= 0) {
        return 'Take profit must result in a positive PnL';
      }
      if (side === 'long' && triggerPrice <= entryPrice) {
        return 'Take profit price must be above entry price for long positions';
      }
      if (side === 'short' && triggerPrice >= entryPrice) {
        return 'Take profit price must be below entry price for short positions';
      }
    } else {
      if (pnl >= 0) {
        return 'Stop loss must result in a negative PnL';
      }
      if (side === 'long' && triggerPrice >= entryPrice) {
        return 'Stop loss price must be below entry price for long positions';
      }
      if (side === 'short' && triggerPrice <= entryPrice) {
        return 'Stop loss price must be above entry price for short positions';
      }
    }

    if (executionType === 'limit') {
      const execPrice = parseFloat(executionPrice);
      if (!execPrice || execPrice <= 0) {
        return 'Please enter a valid execution price';
      }
    }

    return null;
  };

  const handleConfirm = () => {
    const error = validateTPSL();
    if (error) {
      alert(error);
      return;
    }

    const execPrice = executionType === 'limit' && executionPrice
      ? parseFloat(executionPrice)
      : undefined;

    onConfirm(calculatedValues.triggerPrice, executionType, execPrice);
    onClose();
  };

  if (!isOpen) return null;

  const primaryColor = isTP ? { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: '#16a34a', gradient: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' }
    : { bg: 'rgba(220, 38, 38, 0.1)', border: 'rgba(220, 38, 38, 0.3)', text: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' };

  const sliderColor = isTP ? '#16a34a' : '#dc2626';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '24px',
          padding: '28px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          maxWidth: '420px',
          width: '100%'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: primaryColor.bg,
                border: `1px solid ${primaryColor.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isTP ? (
                <TrendingUp size={20} style={{ color: primaryColor.text }} />
              ) : (
                <TrendingDown size={20} style={{ color: primaryColor.text }} />
              )}
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: primaryColor.text }}>
              {isTP ? 'Take Profit' : 'Stop Loss'}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
            }}
          >
            <X size={18} style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
          </button>
        </div>

        <div
          style={{
            background: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px'
          }}
        >
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '13px' }}>Side:</span>
              <span style={{
                marginLeft: '8px',
                fontWeight: 600,
                color: side === 'long' ? '#16a34a' : '#dc2626'
              }}>
                {side.toUpperCase()}
              </span>
            </div>
            <div>
              <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '13px' }}>Entry:</span>
              <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontWeight: 600, color: '#000' }}>
                ${entryPrice.toFixed(2)}
              </span>
            </div>
            <div>
              <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '13px' }}>Amount:</span>
              <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontWeight: 600, color: '#000' }}>
                {amount.toFixed(6)}
              </span>
            </div>
            <div>
              <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '13px' }}>Leverage:</span>
              <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontWeight: 600, color: '#000' }}>
                {leverage}x
              </span>
            </div>
          </div>
        </div>

        <div className="mb-5">
          <div className="flex gap-2 mb-4">
            {(['price', 'pnl', 'percentage'] as InputMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setInputMode(mode)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: inputMode === mode ? `2px solid ${primaryColor.border}` : '1px solid rgba(0, 0, 0, 0.08)',
                  background: inputMode === mode ? primaryColor.bg : 'rgba(0, 0, 0, 0.02)',
                  color: inputMode === mode ? primaryColor.text : 'rgba(0, 0, 0, 0.6)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                {mode === 'price' && <DollarSign size={14} />}
                {mode === 'percentage' && <Percent size={14} />}
                {mode === 'price' ? 'Price' : mode === 'pnl' ? 'PnL' : '%'}
              </button>
            ))}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', color: 'rgba(0, 0, 0, 0.5)', marginBottom: '8px', fontWeight: 500 }}>
              {inputMode === 'price' ? 'Trigger Price' : inputMode === 'pnl' ? 'Target PnL (USD)' : 'Target ROI (%)'}
            </label>
            <input
              type="text"
              value={inputMode === 'price' ? priceValue : inputMode === 'pnl' ? pnlValue : percentageValue}
              onChange={(e) => handleInputChange(inputMode, e.target.value)}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '12px',
                border: `1px solid ${primaryColor.border}`,
                background: 'rgba(255, 255, 255, 0.8)',
                fontSize: '15px',
                fontFamily: 'monospace',
                fontWeight: 500,
                color: '#000',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = `2px solid ${primaryColor.border}`;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor.bg}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = `1px solid ${primaryColor.border}`;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {isValid && (
          <div
            style={{
              background: primaryColor.bg,
              border: `1px solid ${primaryColor.border}`,
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '20px'
            }}
          >
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>Trigger Price:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#000' }}>
                  ${calculatedValues.triggerPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>Est. PnL:</span>
                <span style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: calculatedValues.pnl >= 0 ? '#16a34a' : '#dc2626'
                }}>
                  ${calculatedValues.pnl.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>Price Change:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#000' }}>
                  {calculatedValues.percentage.toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>ROI:</span>
                <span style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: calculatedValues.roi >= 0 ? '#16a34a' : '#dc2626'
                }}>
                  {calculatedValues.roi.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <label style={{ display: 'block', fontSize: '13px', color: 'rgba(0, 0, 0, 0.5)', marginBottom: '8px', fontWeight: 500 }}>
            Execution Type
          </label>
          <select
            value={executionType}
            onChange={(e) => setExecutionType(e.target.value as 'market' | 'limit')}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '12px',
              border: `1px solid ${primaryColor.border}`,
              background: 'rgba(255, 255, 255, 0.8)',
              fontSize: '15px',
              fontWeight: 500,
              color: '#000',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </select>
          {executionType === 'limit' && (
            <input
              type="text"
              value={executionPrice}
              onChange={(e) => setExecutionPrice(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="Execution Price"
              style={{
                width: '100%',
                marginTop: '8px',
                padding: '14px 16px',
                borderRadius: '12px',
                border: `1px solid ${primaryColor.border}`,
                background: 'rgba(255, 255, 255, 0.8)',
                fontSize: '15px',
                fontFamily: 'monospace',
                fontWeight: 500,
                color: '#000',
                outline: 'none'
              }}
            />
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '14px',
              background: 'rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              color: 'rgba(0, 0, 0, 0.7)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(0, 'market');
              onClose();
            }}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '14px',
              background: 'rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              color: 'rgba(0, 0, 0, 0.7)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.06)';
            }}
          >
            Remove
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '14px',
              background: isValid ? primaryColor.gradient : 'rgba(0, 0, 0, 0.1)',
              border: 'none',
              color: isValid ? '#ffffff' : 'rgba(0, 0, 0, 0.4)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: isValid ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              boxShadow: isValid ? `0 8px 20px ${primaryColor.bg}` : 'none'
            }}
            onMouseEnter={(e) => {
              if (isValid) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 12px 28px ${primaryColor.bg}`;
              }
            }}
            onMouseLeave={(e) => {
              if (isValid) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 8px 20px ${primaryColor.bg}`;
              }
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default TakeProfitStopLossModal;
